import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faExclamationTriangle,
	faPrint,
	faChevronLeft,
	faChevronRight,
} from "@fortawesome/free-solid-svg-icons"
import Loader from "../basics/Loader"
import { useGroups } from "../../hooks/groups"
import { fetchAtelierState } from "../../store/actions/atelierActions"
import { setFeuilletage } from "../../store/actions/feuilletageActions"
import { atelierEtat } from "../PDF/AtelierEtat"
import { COLORS } from "../../utilities/Constants"
import { formatNumber, round20 } from "../../utilities/Addons"
import { generalStyles } from "../styles/styles"
import { tableStyles } from "../styles/tableStyles"
import { BadgeIssueDepannage } from "./AtelierIssueDepannageV2"

// Menus du tableau de bord /atelier/etat. `groups` = liste des groupes autorisés
// (null = visible par tous) ; le backend (AtelierStateView) reste la SOURCE DE
// VÉRITÉ et applique le même gating + 403. ⚠️ Miroir EXACT de
// ATELIER_STATE_MENUS (backend/atelier/views.py) : les deux bougent ensemble.
const ETAT_MENUS = [
	{ key: "global", label: "Vue d'ensemble", groups: null },
	{
		key: "devisAFaire",
		label: "Devis à faire",
		groups: ["responsable_atelier"],
	},
	{ key: "devis", label: "Devis", groups: ["responsable_atelier"] },
	{
		key: "devisEnAttente",
		label: "Devis en attente",
		groups: ["responsable_atelier"],
	},
	{
		key: "devisRefuses",
		label: "Devis refusés",
		groups: ["responsable_atelier"],
	},
	{
		key: "travauxACommencer",
		label: "Travaux à commencer",
		groups: ["responsable_atelier"],
	},
	{ key: "mesTravaux", label: "Mes travaux", groups: ["employe"] },
	{
		key: "travauxTermines",
		label: "Travaux terminés",
		groups: ["responsable_atelier"],
	},
	{
		key: "aRestituer",
		label: "À restituer",
		groups: ["responsable_atelier"],
	},
	{
		key: "controleAvantFacturation",
		label: "Contrôle avant facturation",
		groups: ["admin", "assistant_admin", "responsable_atelier"],
	},
	{
		key: "okPourFacturation",
		label: "Ok pour facturation",
		groups: ["admin", "assistant_admin"],
	},
	{
		key: "enAttentePaiement",
		label: "En attente de paiement",
		groups: ["admin", "assistant_admin"],
	},
	{ key: "clos", label: "Clos", groups: ["admin", "assistant_admin"] },
]

const pagerBtnStyle = (disabled) => ({
	width: 34,
	height: 34,
	borderRadius: 9,
	border: `1px solid ${COLORS.border}`,
	backgroundColor: COLORS.surface,
	color: disabled ? COLORS.textLight : COLORS.text,
	cursor: disabled ? "not-allowed" : "pointer",
	fontFamily: "inherit",
	fontSize: 13,
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
})

const AtelierEtatV2 = () => {
	const dispatch = useDispatch()
	const history = useHistory()
	const { atelierInfo } = useSelector((state) => state)
	const { atelierState, atelier, atelierStatePagination } = atelierInfo
	const { loading } = atelierInfo.fetchAtelierState
	const { hasGroup } = useGroups()

	const [menu, setMenu] = useState("global")
	const [page, setPage] = useState(1)
	const [hover, setHover] = useState(null)
	const [plaqueFilter, setPlaqueFilter] = useState("")

	const {
		count = 0,
		numPages = 1,
		pageSize = 50,
	} = atelierStatePagination || {}

	// Filtre local par plaques : ne filtre QUE les fiches déjà chargées (page
	// courante), sans requête serveur. On conserve le numéro absolu d'origine.
	const filteredState = useMemo(() => {
		const rows = atelierState.map((fiche, index) => ({
			fiche,
			num: (page - 1) * pageSize + index + 1,
		}))
		const q = plaqueFilter.trim().toLowerCase()
		if (!q) return rows
		return rows.filter(({ fiche }) =>
			(fiche.plaques || "").toLowerCase().includes(q),
		)
	}, [atelierState, plaqueFilter, page, pageSize])

	// Somme des totaux des fiches affichées (page courante, filtre appliqué).
	// Arrondi au 5 centimes (round20) LIGNE PAR LIGNE avant la somme : le total
	// correspond ainsi exactement à l'addition des montants affichés.
	const totalSum = useMemo(
		() =>
			filteredState.reduce(
				(acc, { fiche }) => acc + round20(fiche.total || 0),
				0,
			),
		[filteredState],
	)

	// Menus visibles = ceux dont l'utilisateur a au moins un groupe autorisé.
	const visibleMenus = useMemo(
		() => ETAT_MENUS.filter((m) => hasGroup(m.groups)),
		[hasGroup],
	)

	const enterListener = (e) => {
		if (e.key === "Enter") history.push("/")
	}
	const openFiche = (id) => {
		// Liste de feuilletage = les fiches AFFICHÉES (page courante + filtre
		// plaques). Avant, cet écran posait un state routeur que le feuilletage
		// ne lisait pas : Page Up/Down y feuilletait la dernière RECHERCHE.
		dispatch(
			setFeuilletage(
				"ate",
				filteredState.map(({ fiche }) => fiche.id),
				"atelierState",
			),
		)
		history.push(`/atelier/fiche/${id}`)
	}
	const clickMenu = (_menu) => {
		setMenu(_menu)
		setPage(1)
		setPlaqueFilter("")
		dispatch(fetchAtelierState(_menu, 1))
	}
	const goToPage = (p) => {
		if (p < 1 || p > numPages || p === page) return
		setPage(p)
		dispatch(fetchAtelierState(menu, p))
	}
	const labelNothing = useMemo(() => {
		switch (menu) {
			case "devis":
			case "devisAFaire":
			case "devisEnAttente":
			case "devisRefuses":
				return "Aucun devis trouvé"
			case "mesTravaux":
				return "Vous n'avez pas de travaux pour l'instant"
			default:
				return "Aucune fiche trouvée"
		}
	}, [menu])

	const rowBackground = (fiche, i) => {
		if (atelier.id === fiche.id) return COLORS.accentLight
		if (hover === fiche.id) return COLORS.bg
		return tableStyles.rowBg(i)
	}

	useEffect(() => {
		dispatch(fetchAtelierState(menu))
		document.addEventListener("keyup", enterListener)
		return () => {
			document.removeEventListener("keyup", enterListener)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<div style={generalStyles.page}>
			<div style={{ maxWidth: 1400, margin: "0 auto" }}>
				<h2
					style={{
						...generalStyles.title,
						textAlign: "left",
						marginBottom: 24,
					}}
				>
					Etat de l'Atelier
				</h2>

				{/* Sélecteur principal (segmented control) — entrées gatées par groupe */}
				<div
					style={{
						...generalStyles.segmented,
						display: "flex",
						justifyContent: "center",
					}}
				>
					{visibleMenus.map((m) => (
						<button
							key={m.key}
							type="button"
							onClick={() => clickMenu(m.key)}
							style={generalStyles.segment(menu === m.key)}
						>
							{m.label}
						</button>
					))}
				</div>

				{loading ? (
					<Loader variant="ring" message="Chargement…" />
				) : atelierState.length > 0 ? (
					<>
						{/* Total + filtre local par plaques (fiches de la page courante) */}
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: 12,
								marginTop: 20,
								marginBottom: 12,
								flexWrap: "wrap",
							}}
						>
							<span
								style={{
									fontSize: 13,
									color: COLORS.textMuted,
									fontVariantNumeric: "tabular-nums",
								}}
							>
								{plaqueFilter.trim()
									? `${filteredState.length} / ${count} fiches`
									: `${count} fiches`}
							</span>
							<input
								type="text"
								value={plaqueFilter}
								onChange={(e) =>
									setPlaqueFilter(e.target.value)
								}
								placeholder="Filtrer par plaques…"
								style={{
									...generalStyles.input,
									flex: "0 0 auto",
									width: 200,
									margin: 0,
								}}
							/>
						</div>

						<div style={tableStyles.container}>
							<table style={tableStyles.table}>
								<thead>
									<tr>
										<th
											style={{
												...tableStyles.th,
												width: 48,
											}}
										>
											N°
										</th>
										<th style={tableStyles.th}>Etat</th>
										<th style={tableStyles.th}>Client</th>
										<th style={tableStyles.th}>Plaques</th>
										<th style={tableStyles.th}>Marque</th>
										<th style={tableStyles.th}>Modèle</th>
										<th
											style={{
												...tableStyles.th,
												textAlign: "right",
											}}
										>
											Total
										</th>
										<th style={tableStyles.th}>
											Depuis le
										</th>
									</tr>
								</thead>
								<tbody>
									{filteredState.map(
										({ fiche, num }, index) => (
											<tr
												key={fiche.id}
												onDoubleClick={() =>
													openFiche(fiche.id)
												}
												onMouseEnter={() =>
													setHover(fiche.id)
												}
												onMouseLeave={() =>
													setHover(null)
												}
												style={{
													cursor: "pointer",
													backgroundColor:
														rowBackground(
															fiche,
															index,
														),
												}}
											>
												<td style={tableStyles.td}>
													{num}
												</td>
												<td style={tableStyles.td}>
													{fiche.etat}
												</td>
												<td
													style={{
														...tableStyles.td,
														textAlign: "left",
													}}
												>
													{fiche.is_client_incomplet && (
														<FontAwesomeIcon
															icon={
																faExclamationTriangle
															}
															title="Fiche client incomplète"
															style={{
																color: COLORS.warning,
																marginRight: 8,
															}}
														/>
													)}
													{fiche.client_label}
													<BadgeIssueDepannage
														issue={
															fiche.issue_depannage
														}
													/>
												</td>
												<td style={tableStyles.td}>
													{fiche.plaques}
												</td>
												<td style={tableStyles.td}>
													{fiche.marque}
												</td>
												<td style={tableStyles.td}>
													{fiche.modele}
												</td>
												<td
													style={{
														...tableStyles.td,
														textAlign: "right",
														fontVariantNumeric:
															"tabular-nums",
													}}
												>
													{formatNumber(
														round20(
															fiche.total || 0,
														),
													)}
												</td>
												<td style={tableStyles.td}>
													{fiche.date}
												</td>
											</tr>
										),
									)}
								</tbody>
								<tfoot>
									<tr>
										<td
											style={{
												...tableStyles.td,
												textAlign: "right",
												fontWeight: 600,
											}}
											colSpan={6}
										>
											Total affiché
										</td>
										<td
											style={{
												...tableStyles.td,
												textAlign: "right",
												fontWeight: 600,
												fontVariantNumeric:
													"tabular-nums",
											}}
										>
											{/* round20 idempotent (somme de multiples de
										    0.05) — gomme juste le bruit flottant. */}
											{formatNumber(round20(totalSum))}
										</td>
										<td style={tableStyles.td} />
									</tr>
								</tfoot>
							</table>
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 16,
								marginTop: 20,
							}}
						>
							{numPages > 1 && (
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 12,
									}}
								>
									<button
										type="button"
										title="Page précédente"
										disabled={page <= 1}
										onClick={() => goToPage(page - 1)}
										style={pagerBtnStyle(page <= 1)}
									>
										<FontAwesomeIcon icon={faChevronLeft} />
									</button>
									<span
										style={{
											fontSize: 13,
											color: COLORS.textMuted,
											fontVariantNumeric: "tabular-nums",
											whiteSpace: "nowrap",
										}}
									>
										Page {page} / {numPages} · {count}{" "}
										fiches
									</span>
									<button
										type="button"
										title="Page suivante"
										disabled={page >= numPages}
										onClick={() => goToPage(page + 1)}
										style={pagerBtnStyle(page >= numPages)}
									>
										<FontAwesomeIcon
											icon={faChevronRight}
										/>
									</button>
								</div>
							)}
							<button
								type="button"
								style={generalStyles.btnSecondary}
								onClick={() =>
									atelierEtat(
										filteredState.map((r) => r.fiche),
									)
								}
							>
								<FontAwesomeIcon
									icon={faPrint}
									style={{ marginRight: 8 }}
								/>
								Imprimer
							</button>
						</div>
					</>
				) : (
					<div style={tableStyles.empty}>{labelNothing}</div>
				)}
			</div>
		</div>
	)
}

export default AtelierEtatV2
